import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiCall, API_ENDPOINTS } from '../utils/api';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState({
    id: 'user1', // Mock user ID - in production get from authentication
    balance: 0,
    pendingBalance: 0,
    totalDeposited: 0,
    totalWithdrawn: 0,
    rentedAsics: [],
    pendingDeposits: [],
    totalMiningPower: 0,
    totalMiningReward: 0,
    dailyEarnings: 0
  });

  // Load user balance on component mount
  useEffect(() => {
    loadUserBalance();
    loadUserRentals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user balance from API
  const loadUserBalance = async () => {
    try {
      const response = await apiCall(API_ENDPOINTS.USER_BALANCE(user.id));
      if (response.success) {
        setUser(prevUser => ({
          ...prevUser,
          balance: response.data.balance || 0,
          pendingBalance: response.data.pending_balance || 0,
          totalDeposited: response.data.total_deposited || 0,
          totalWithdrawn: response.data.total_withdrawn || 0
        }));
      }
    } catch (error) {
        // Silent fail for now as CORS/Connection issues are expected during dev
        // console.error('Failed to load user balance:', error); 
    }
  };

  // Load user rentals from API
  const loadUserRentals = async () => {
    try {
      const response = await apiCall(API_ENDPOINTS.USER_RENTALS(user.id));
      if (response.success) {
        setUser(prevUser => ({
          ...prevUser,
          rentedAsics: response.data || []
        }));
      }
    } catch (error) {
       // Silent fail for now as CORS/Connection issues are expected during dev
       // console.error('Failed to load user rentals:', error);
    }
  };


  // Calculate mining power and rewards based on rented ASICs
  const calculateMiningStats = (rentedAsics) => {
    let totalHashrate = 0;
    let totalDailyEarnings = 0;
    let totalReward = 0;

    rentedAsics.forEach(rental => {
      if (rental.status === 'active') {
        totalHashrate += parseFloat(rental.hashrate) || 0;
        totalDailyEarnings += parseFloat(rental.daily_earnings_estimate) || 0;
        
        // Calculate total reward based on days active and remaining
        const daysActive = Math.floor((Date.now() - new Date(rental.start_date).getTime()) / (1000 * 60 * 60 * 24));
        const totalDays = rental.duration_days;
        const remainingDays = Math.max(0, totalDays - daysActive);
        
        totalReward += (daysActive * (rental.daily_earnings_estimate || 0)) + (remainingDays * (rental.daily_earnings_estimate || 0));
      }
    });

    return {
      totalMiningPower: totalHashrate,
      totalMiningReward: totalReward,
      dailyEarnings: totalDailyEarnings
    };
  };

  // Update mining stats when rented ASICs change
  useEffect(() => {
    const stats = calculateMiningStats(user.rentedAsics);
    setUser(prevUser => ({
      ...prevUser,
      ...stats
    }));
  }, [user.rentedAsics]);

  // Add a new rental
  const addRental = async (rentalData) => {
    try {
      const response = await apiCall('rentals.php', 'POST', {
        action: 'create_rental',
        user_id: user.id,
        asic_model_id: rentalData.asicModelId,
        duration_days: rentalData.duration,
        quantity: rentalData.quantity || 1
      });

      if (response.success) {
        // Reload user balance and rentals
        await loadUserBalance();
        await loadUserRentals();
        return response;
      }
    } catch (error) {
      console.error('Failed to create rental:', error);
      throw error;
    }
  };

  // Add a deposit request
  const addDeposit = async (depositData) => {
    try {
      const response = await apiCall('deposits.php', 'POST', {
        action: 'create_deposit',
        user_id: user.id,
        amount_usd: depositData.amount,
        payment_method: depositData.paymentMethod || 'btc',
        crypto_amount: depositData.cryptoAmount
      });

      if (response.success) {
        // Reload user balance
        await loadUserBalance();
        return response;
      }
    } catch (error) {
      console.error('Failed to create deposit:', error);
      throw error;
    }
  };

  // Confirm deposit (for admin use)
  const confirmDeposit = async (depositId, confirmedAmount) => {
    try {
      const response = await apiCall('deposits.php', 'POST', {
        action: 'confirm_deposit',
        deposit_id: depositId,
        admin_id: 'admin1', // Mock admin ID
        status: 'confirmed'
      });

      if (response.success) {
        // Reload user balance
        await loadUserBalance();
        return response;
      }
    } catch (error) {
      console.error('Failed to confirm deposit:', error);
      throw error;
    }
  };

  // Check if user has sufficient balance
  const hasSufficientBalance = (amount) => {
    return user.balance >= amount;
  };

  // Get ASIC specifications by model
  const getAsicSpecs = async (modelId) => {
    try {
      const response = await apiCall(`rentals.php?action=asic_models`);
      if (response.success) {
        const model = response.data.find(asic => asic.id === modelId);
        return model || { hashrate: 0, daily_earnings_estimate: 0 };
      }
    } catch (error) {
      console.error('Failed to get ASIC specs:', error);
    }
    return { hashrate: 0, daily_earnings_estimate: 0 };
  };

  // Get available ASIC models
  const getAvailableAsics = async () => {
    try {
      const response = await apiCall('rentals.php?action=available_asics');
      if (response.success) {
        return response.data;
      }
    } catch (error) {
      console.error('Failed to get available ASICs:', error);
    }
    return [];
  };

  // Get pending deposits for admin
  const getPendingDeposits = async () => {
    try {
      const response = await apiCall('deposits.php?action=pending_deposits');
      if (response.success) {
        return response.data;
      }
    } catch (error) {
      console.error('Failed to get pending deposits:', error);
    }
    return [];
  };

  const contextValue = {
    user,
    setUser,
    addRental,
    addDeposit,
    confirmDeposit,
    hasSufficientBalance,
    getAsicSpecs,
    getAvailableAsics,
    getPendingDeposits,
    calculateMiningStats,
    loadUserBalance,
    loadUserRentals,
    apiCall
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};